import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import useFeatureFlags from '../hooks/useFeatureFlags';
import SEO from '../components/SEO';

// Sprint R1 — public mass entry. Paste any deck export / want list and get an
// optimized cart: condition preference, per-line variant override, restock
// alerts on misses. Public on purpose — this is an acquisition funnel.
const PREFS = [
  { key: 'cheapest', label: 'Cheapest' },
  { key: 'best_condition', label: 'Best condition' },
  { key: 'nm_only', label: 'NM only' },
];

const SAMPLE = `4 Iono PAL 185
2 Charizard ex OBF 125
3 Boss's Orders
Ultra Ball x4`;

export default function MassEntry() {
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const { addToCart } = useCart();
  const [text, setText] = useState('');
  const [preference, setPreference] = useState('cheapest');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [overrides, setOverrides] = useState({}); // lineIndex -> candidate index
  const [msg, setMsg] = useState('');
  const [alerted, setAlerted] = useState({});     // product_id -> true
  const [alertEmail, setAlertEmail] = useState('');

  const lineCount = useMemo(
    () => text.split(/\r?\n/).filter(l => l.trim() && !/^(#|\/\/)/.test(l.trim())).length,
    [text]
  );

  if (flags.mass_entry_enabled === false) {
    return (
      <div className="page" style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
        <h1>Not available</h1>
        <p>Mass entry is currently turned off. <Link to="/shop">Browse the shop</Link> instead.</p>
      </div>
    );
  }

  const resolve = async (pref = preference) => {
    setBusy(true); setMsg(''); setResult(null); setOverrides({});
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const r = await fetch('/api/search/mass-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text, preference: pref }),
      });
      const j = await r.json();
      if (!r.ok) { setMsg(j.error || 'Failed to resolve list'); return; }
      setResult(j);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const switchPref = (key) => {
    setPreference(key);
    if (result) resolve(key);
  };

  // The shopper can override the optimizer's pick per line.
  const effectiveLine = (line, i) => {
    const ovr = overrides[i];
    if (ovr == null || !line.candidates?.[ovr]) return line;
    const c = line.candidates[ovr];
    return {
      ...line,
      product_id: c.product_id, variant_id: c.variant_id, condition: c.condition,
      label: c.label, unit_price: c.unit_price, available_stock: c.stock,
      quantity: Math.min(line.requested_quantity, c.stock),
    };
  };

  const effectiveLines = result ? result.lines.map(effectiveLine) : [];
  const subtotal = effectiveLines.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  const addAll = async () => {
    if (!effectiveLines.length) return;
    setBusy(true);
    let ok = 0;
    for (const line of effectiveLines) {
      try {
        const r = await addToCart({
          id: line.product_id,
          variant_id: line.variant_id || undefined,
          name: line.matched_name,
          price: line.unit_price,
          stock: line.available_stock,
        }, line.quantity);
        if (r?.ok !== false) ok++;
      } catch (_) {}
    }
    setBusy(false);
    setMsg(`Added ${ok}/${effectiveLines.length} cards to cart.`);
    if (ok > 0) setTimeout(() => navigate('/cart'), 1200);
  };

  const subscribeRestock = async (u) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const email = alertEmail.trim();
    if (!token && !email) { setMsg('Enter an email below to get restock alerts.'); return; }
    try {
      const r = await fetch('/api/inventory/restock-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ product_id: u.product_id, email: email || undefined }),
      });
      if (r.ok) setAlerted(a => ({ ...a, [u.product_id]: true }));
      else { const j = await r.json().catch(() => ({})); setMsg(j.error || 'Could not set alert'); }
    } catch (e) { setMsg(e.message); }
  };

  const needsEmailForAlerts = !(localStorage.getItem('token') || localStorage.getItem('authToken'))
    && (result?.unresolved || []).some(u => u.error === 'Out of stock' && u.product_id);

  return (
    <div className="page" style={{ maxWidth: 980, margin: '0 auto', padding: 24 }}>
      <SEO
        title="Mass entry — paste your card list"
        description="Paste a deck list or want list and build an optimized cart in one click. Condition preferences, instant pricing, restock alerts on misses."
      />
      <h1>Mass entry</h1>
      <p style={{ color: '#64748b' }}>
        Paste a deck export or want list — one card per line. We accept
        TCG Live exports (<code>4 Iono PAL 185</code>), <code>Ultra Ball x4</code>,
        or plain names. Headers and comments are skipped automatically.
      </p>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={SAMPLE}
        rows={10}
        aria-label="Card list"
        style={{ width: '100%', padding: 12, fontFamily: 'monospace', fontSize: 14, border: '1px solid #e2e8f0', borderRadius: 6 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
        <div role="group" aria-label="Condition preference" style={{ display: 'flex', gap: 6 }}>
          {PREFS.map(p => (
            <button
              key={p.key}
              className={`btn btn-sm ${preference === p.key ? 'btn-primary' : ''}`}
              onClick={() => switchPref(p.key)}
              aria-pressed={preference === p.key}
            >{p.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <small style={{ color: '#64748b' }}>{lineCount} line(s)</small>
          <button className="btn btn-primary" onClick={() => resolve()} disabled={busy || !lineCount}>
            {busy ? 'Resolving…' : 'Resolve & preview'}
          </button>
        </div>
      </div>

      {msg && <div role="status" style={{ marginTop: 12, padding: 10, background: '#fff8e1', borderRadius: 6 }}>{msg}</div>}

      {result && (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ marginBottom: 4 }}>Preview</h2>
          <p style={{ color: '#64748b', marginTop: 0 }}>
            {result.summary.resolved} of {result.summary.total_lines} matched
            {result.skipped > 0 && <> · {result.skipped} header/comment line(s) skipped</>}
            {result.summary.savings_vs_nm > 0 && preference === 'cheapest' &&
              <> · saving <strong>${result.summary.savings_vs_nm.toFixed(2)}</strong> vs all-Near-Mint</>}
          </p>

          {effectiveLines.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 560 }}>
                <thead><tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th align="left">Card</th><th align="left">Condition / printing</th>
                  <th align="right">Qty</th><th align="right">Unit</th><th align="right">Line</th>
                </tr></thead>
                <tbody>
                  {result.lines.map((line, i) => {
                    const eff = effectiveLine(line, i);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td>
                          <Link to={`/product/${eff.product_id}`}>{eff.matched_name}</Link>
                          {line.input !== eff.matched_name &&
                            <small style={{ display: 'block', color: '#94a3b8' }}>{line.input}</small>}
                        </td>
                        <td>
                          {line.candidates.length > 1 ? (
                            <select
                              value={overrides[i] ?? line.candidates.findIndex(c =>
                                c.product_id === line.product_id && c.variant_id === line.variant_id)}
                              onChange={e => setOverrides(o => ({ ...o, [i]: Number(e.target.value) }))}
                              aria-label={`Variant for ${eff.matched_name}`}
                            >
                              {line.candidates.map((c, ci) => (
                                <option key={ci} value={ci}>
                                  {c.label}{c.set_name ? ` · ${c.set_name}` : ''} — ${c.unit_price.toFixed(2)}
                                </option>
                              ))}
                            </select>
                          ) : eff.label}
                        </td>
                        <td align="right">
                          <strong>{eff.quantity}</strong>
                          {eff.quantity < eff.requested_quantity &&
                            <small style={{ color: '#f59e0b', display: 'block' }}>of {eff.requested_quantity}</small>}
                        </td>
                        <td align="right">${eff.unit_price.toFixed(2)}</td>
                        <td align="right">${(eff.unit_price * eff.quantity).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {result.unresolved.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ color: '#dc2626', marginBottom: 6 }}>Couldn't match ({result.unresolved.length})</h3>
              <ul style={{ paddingLeft: 18 }}>
                {result.unresolved.map((u, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <code>{u.input}</code> — {u.error}
                    {u.error === 'Out of stock' && u.product_id && (
                      alerted[u.product_id]
                        ? <small style={{ color: '#16a34a' }}> ✓ alert set</small>
                        : <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => subscribeRestock(u)}>
                            Notify me on restock
                          </button>
                    )}
                    {u.suggestions?.length > 0 && (
                      <small style={{ display: 'block', color: '#64748b' }}>
                        Did you mean: {u.suggestions.join(' · ')}
                      </small>
                    )}
                  </li>
                ))}
              </ul>
              {needsEmailForAlerts && (
                <input
                  type="email"
                  placeholder="email for restock alerts"
                  value={alertEmail}
                  onChange={e => setAlertEmail(e.target.value)}
                  style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, maxWidth: 320, width: '100%' }}
                />
              )}
            </div>
          )}

          <div style={{ marginTop: 18, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={addAll} disabled={busy || !effectiveLines.length}>
              {busy ? 'Adding…' : `Add ${effectiveLines.length} card(s) — $${subtotal.toFixed(2)}`}
            </button>
            <button className="btn" onClick={() => { setResult(null); setOverrides({}); }}>Clear</button>
          </div>
        </section>
      )}
    </div>
  );
}
